import React from 'react';
import { Link } from 'react-router-dom';
import { chainName, nativeToken } from '../../constant/constant';
import { DOCS_PATH, JOBS_PORTAL_PATH } from '../../config/paths';
import { DocChartPlaceholder } from './components/DocDiagrams';
import { docsChartCatalog } from './docsChartCatalog';

const networkLabel = chainName || 'Arc';
const nativeLabel = nativeToken || 'USDC';

export function DocProse({ children }) {
    return (
        <div className="docs-prose space-y-6 text-[15px] leading-[1.75] text-zinc-600 dark:text-zinc-400">
            {children}
        </div>
    );
}

export function DocH2({ children }) {
    return (
        <h2 className="mt-12 scroll-mt-24 border-t border-zinc-100 pt-10 text-xl font-bold tracking-tight text-zinc-900 first:mt-0 first:border-0 first:pt-0 dark:border-zinc-800/80 dark:text-white">
            {children}
        </h2>
    );
}

export function DocLead({ children }) {
    return (
        <p className="text-base leading-[1.75] text-zinc-600 dark:text-zinc-400 sm:text-[17px]">
            {children}
        </p>
    );
}

export function DocList({ items }) {
    return (
        <ul className="ml-1 list-none space-y-2">
            {items.map((item) => (
                <li key={item} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

export function DocCallout({ title, children, tone = 'teal' }) {
    const tones = {
        teal: 'border-teal-200 bg-teal-50/80 dark:border-teal-800/50 dark:bg-teal-950/30',
        amber: 'border-amber-200 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/30',
    };
    return (
        <div className={`rounded-xl border px-4 py-3 ${tones[tone] || tones.teal}`}>
            {title ? (
                <p className="mb-1 text-sm font-semibold text-zinc-900 dark:text-white">{title}</p>
            ) : null}
            <div className="text-sm text-zinc-600 dark:text-zinc-400">{children}</div>
        </div>
    );
}

const welcomeContent = (
    <>
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">
            User guide
        </p>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl lg:text-5xl">
            Welcome to <span className="text-teal-600 dark:text-teal-400">CronPay</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-[1.75] text-zinc-600 dark:text-zinc-400 sm:text-lg">
            Send and receive {nativeLabel} on {networkLabel}, lock funds until work is done, and manage
            team tasks with escrow, all from one app, with your own wallet in control.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
            {['Direct Pay', 'Tracked escrow', 'Get Paid', 'Company & Job'].map((tag) => (
                <span
                    key={tag}
                    className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                >
                    {tag}
                </span>
            ))}
        </div>

        <DocChartPlaceholder chartId="platform-overview" />

        <div className="mt-10 flex flex-wrap gap-3">
            <Link
                to={`${DOCS_PATH}/quick-start`}
                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-500"
            >
                Get started
                <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
            <Link
                to={`${DOCS_PATH}/payment-types`}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-800 transition hover:border-zinc-400 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
            >
                Payment types
            </Link>
        </div>

        <div className="mt-16">
            <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">
                What you can do
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
                {[
                    {
                        icon: 'fa-solid fa-bolt',
                        title: 'Instant payments',
                        desc: 'Pay someone directly from your wallet with a clear on-chain record.',
                    },
                    {
                        icon: 'fa-solid fa-lock',
                        title: 'Tracked payments',
                        desc: 'Lock funds and release them over time or when milestones are met.',
                    },
                    {
                        icon: 'fa-solid fa-inbox',
                        title: 'Payment requests',
                        desc: 'Ask to be paid; the payer accepts and sends in one step.',
                    },
                    {
                        icon: 'fa-solid fa-briefcase',
                        title: 'Company & Job',
                        desc: 'Assign tasks, fund escrow, review work, and pay your team.',
                    },
                ].map((card) => (
                    <div
                        key={card.title}
                        className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/50"
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400">
                            <i className={`${card.icon} text-sm`} />
                        </span>
                        <h3 className="mt-3 font-semibold text-zinc-900 dark:text-white">{card.title}</h3>
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{card.desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </>
);

export const docsPages = {
    welcome: {
        title: 'Welcome',
        isHero: true,
        content: welcomeContent,
    },
    'what-is-cronpay': {
        title: 'What is CronPay',
        content: (
            <DocProse>
                <DocLead>
                    CronPay is a payment hub for people and teams who want to move {nativeLabel} on{' '}
                    {networkLabel} without giving up control of their wallet. You approve every
                    payment; CronPay helps you track, lock, and release funds fairly.
                </DocLead>
                <DocChartPlaceholder chartId="how-cronpay-works" />
                <DocH2>Who it is for</DocH2>
                <DocList
                    items={[
                        'Freelancers and clients paying for completed work',
                        'Companies paying employees or contractors with task-based escrow',
                        'Anyone who wants a clear history of sends, receives, and disputes',
                    ]}
                />
                <DocH2>What makes it different</DocH2>
                <DocList
                    items={[
                        'One dashboard for direct pay, tracked escrow, and payment requests',
                        'Company & Job workspace for tasks, approvals, and team payouts',
                        'Built-in dispute flow when something does not go as agreed',
                    ]}
                />
            </DocProse>
        ),
    },
    network: {
        title: 'Arc Network',
        content: (
            <DocProse>
                <p>
                    CronPay runs on <strong className="text-zinc-800 dark:text-zinc-200">{networkLabel}</strong>.
                    Your wallet must be on this network before you send money or fund a task escrow.
                </p>
                <DocH2>Supported assets</DocH2>
                <p>
                    Payments and escrows use approved tokens on this network, typically {nativeLabel}.
                    The app will show which token you are paying with before you confirm.
                </p>
                <DocCallout title="Wrong network?">
                    If your wallet is on another chain, use the network switcher in the app header or
                    your wallet app to switch to {networkLabel}, then try again.
                </DocCallout>
                <DocH2>Testnet faucet</DocH2>
                <p>
                    On testnet, you may need test {nativeLabel} from a faucet link in the app menu before
                    your first payment.
                </p>
            </DocProse>
        ),
    },
    'payment-types': {
        title: 'Payment Types',
        content: (
            <DocProse>
                <DocLead>
                    Pick the flow that matches how much control each side needs before money moves.
                </DocLead>
                <DocChartPlaceholder chartId="payment-types-comparison" />
                <DocH2>Direct payment</DocH2>
                <p>
                    Send {nativeLabel} straight to another wallet in one step. Best for invoices, tips,
                    or when both sides agree payment is due now.
                </p>
                <DocH2>Tracked (locked) payment</DocH2>
                <p>
                    Money is held safely until milestones pass or the receiver claims their share over
                    time. Either side can raise a dispute while funds are locked.
                </p>
                <DocH2>Payment request</DocH2>
                <p>
                    You ask to be paid; the payer reviews and accepts. When they accept, the payment is
                    sent from their wallet. This is useful in the Get Paid section.
                </p>
                <DocH2>Task escrow (Company & Job)</DocH2>
                <p>
                    A company locks funds for a specific task. The employee completes the work; the
                    company approves and releases payout from escrow.
                </p>
            </DocProse>
        ),
    },
    'quick-start': {
        title: 'Quick Start Guide',
        content: (
            <DocProse>
                <DocChartPlaceholder chartId="onboarding-steps" />
                <DocH2>1. Connect your wallet</DocH2>
                <p>
                    Open CronPay and tap <strong className="text-zinc-800 dark:text-zinc-200">Connect</strong>.
                    Choose Rainbow, MetaMask, or another wallet on {networkLabel}. On mobile, opening the
                    site inside your wallet app’s browser often works best.
                </p>
                <DocH2>2. Choose what you want to do</DocH2>
                <DocList
                    items={[
                        'Pay: send a direct or tracked payment',
                        'Get Paid: create a payment request',
                        'Company & Job: set up a company or join as an employee',
                    ]}
                />
                <DocH2>3. Confirm in your wallet</DocH2>
                <p>
                    Review the amount and token, approve spending if asked, then confirm the transaction.
                    Status updates appear on your dashboard and in activity history.
                </p>
                <DocCallout title="First time on testnet?">
                    Use the Faucet link in the menu if you need test {nativeLabel} before paying.
                </DocCallout>
            </DocProse>
        ),
    },
    'tracked-payments': {
        title: 'Tracked Payments',
        content: (
            <DocProse>
                <DocLead>
                    Tracked payments hold funds until conditions you set are met, for example time-based
                    milestones. The receiver claims as each part unlocks; disputes are available while
                    money is still locked.
                </DocLead>
                <DocChartPlaceholder
                    chartId="tracked-milestone-timeline"
                    caption="How funds unlock over time for the receiver."
                />
                <DocChartPlaceholder
                    chartId="tracked-payment-flow"
                    caption="From lock to release, including dispute if needed."
                />
                <DocH2>Using tracked payments</DocH2>
                <DocList
                    items={[
                        'Sender starts a tracked payment and locks the full amount',
                        'Receiver claims each unlocked portion from Pay or the dashboard',
                        'When everything is claimed, the payment is complete',
                    ]}
                />
                <DocH2>Where to find them</DocH2>
                <p>
                    Outgoing and incoming tracked payments appear under Pay and on your dashboard.
                    Pending claims show a claim action for the receiver.
                </p>
            </DocProse>
        ),
    },
    'payment-requests': {
        title: 'Payment Requests',
        content: (
            <DocProse>
                <DocLead>
                    From <strong className="text-zinc-800 dark:text-zinc-200">Get Paid</strong>, enter an
                    amount and token, then share the request with your payer. When they accept, payment
                    is sent from their wallet to yours.
                </DocLead>
                <DocChartPlaceholder chartId="payment-request-flow" />
                <DocH2>Request statuses</DocH2>
                <DocList
                    items={[
                        'Pending: waiting for the payer to accept or decline',
                        'Accepted: payment was sent on-chain',
                        'Rejected: the payer declined the request',
                    ]}
                />
            </DocProse>
        ),
    },
    'disputes-overview': {
        title: 'Disputes Overview',
        content: (
            <DocProse>
                <DocLead>
                    A dispute pauses release of locked funds until the situation is reviewed. Either party
                    in a tracked payment or job task can open a dispute when terms are not met.
                </DocLead>
                <DocChartPlaceholder chartId="dispute-touchpoints" />
                <DocH2>Where to manage disputes</DocH2>
                <DocList
                    items={[
                        'Dispute page: payment disputes from the main app',
                        'Company & Job: task disputes for companies and employees',
                        'Support: describe the issue before escalating when possible',
                    ]}
                />
            </DocProse>
        ),
    },
    'resolution-process': {
        title: 'Resolution Process',
        content: (
            <DocProse>
                <DocChartPlaceholder chartId="dispute-resolution-paths" />
                <DocH2>Opening a dispute</DocH2>
                <p>
                    While funds are locked, either party can submit a dispute with a clear explanation.
                    Funds stay frozen until a resolution is recorded.
                </p>
                <DocH2>Outcomes</DocH2>
                <DocList
                    items={[
                        'Release to receiver: funds go to the person who was meant to receive payment',
                        'Refund to sender: funds return to the person who locked them',
                        'Time limits: if rules allow, automatic outcomes may apply after a waiting period',
                    ]}
                />
                <DocCallout title="Tip" tone="amber">
                    Add a clear description and any proof in support chat before opening an on-chain
                    dispute when you can.
                </DocCallout>
            </DocProse>
        ),
    },
    'jobs-portal': {
        title: 'Tasks & Escrow',
        content: (
            <DocProse>
                <DocLead>
                    The Company & Job workspace lets companies assign work, lock payment in escrow, and
                    pay employees when work is approved, with a full task history in the app.
                </DocLead>
                <DocChartPlaceholder chartId="task-escrow-flow" />
                <DocH2>Typical flow</DocH2>
                <DocList
                    items={[
                        'Company registers and adds employees (wallet addresses)',
                        'Create a task with amount and assignee',
                        'Fund escrow from the company wallet',
                        'Employee submits work; company approves and releases payment',
                    ]}
                />
                <p className="mt-4">
                    Open{' '}
                    <Link
                        to={JOBS_PORTAL_PATH}
                        className="font-medium text-teal-700 hover:underline dark:text-teal-400"
                    >
                        Company & Job
                    </Link>{' '}
                    after connecting the wallet you will use for escrow.
                </p>
            </DocProse>
        ),
    },
    'company-workspace': {
        title: 'Company Workspace',
        content: (
            <DocProse>
                <DocChartPlaceholder chartId="workspace-roles" />
                <DocH2>Roles</DocH2>
                <DocList
                    items={[
                        'Company owner: create tasks, fund escrow, approve work, manage the team',
                        'Employee: accept tasks, submit deliverables, receive payout',
                        'Platform admin: helps resolve disputes when needed',
                    ]}
                />
                <DocH2>Main areas</DocH2>
                <DocList
                    items={[
                        'Dashboard: overview and items that need attention',
                        'Tasks: search, filter, and open active or disputed work',
                        'Employees: team roster and status',
                        'History, earnings, and stats: completed work and totals',
                        'Profile: company or employee details',
                    ]}
                />
            </DocProse>
        ),
    },
    'visual-guide': {
        title: 'Diagram guide',
        content: (
            <DocProse>
                <DocLead>
                    This page lists every diagram used in CronPay documentation. Each section below
                    explains what the image should show. When your designs are ready, save them as WebP
                    files and the app will display them automatically.
                </DocLead>
                <DocCallout title="Where to put image files">
                    Save each file as{' '}
                    <strong className="text-zinc-800 dark:text-zinc-200">
                        public/docs/charts/[chart-id].webp
                    </strong>{' '}
                    (for example <span className="font-mono">platform-overview.webp</span>). Use the
                    chart id exactly as listed below. Refresh the docs page to see the image.
                </DocCallout>
                <DocH2>All diagrams</DocH2>
                <div className="space-y-12">
                    {docsChartCatalog.map((chart) => (
                        <div key={chart.id}>
                            <DocChartPlaceholder
                                chartId={chart.id}
                                caption={`Used on: ${chart.usedOn} · Suggested size: ${chart.suggestedSize}`}
                            />
                            <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 text-sm dark:border-zinc-800 dark:bg-zinc-900/40">
                                <p className="font-semibold text-zinc-900 dark:text-white">
                                    Brief for designers
                                </p>
                                <p className="mt-1 text-zinc-600 dark:text-zinc-400">{chart.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </DocProse>
        ),
    },
    'trust-and-safety': {
        title: 'Trust & safety',
        content: (
            <DocProse>
                <DocLead>
                    CronPay is built so you stay in control of your wallet and your funds.
                </DocLead>
                <DocH2>Your wallet, your keys</DocH2>
                <p>
                    CronPay never asks for your seed phrase or private keys. You sign each payment and
                    escrow action in your own wallet app.
                </p>
                <DocH2>Before you pay</DocH2>
                <DocList
                    items={[
                        'Check the recipient address and amount on the confirmation screen',
                        'Use the correct network (' + networkLabel + ')',
                        'For large amounts, send a small test payment first when possible',
                    ]}
                />
                <DocH2>Disputes and support</DocH2>
                <p>
                    Use in-app support or report a problem if something looks wrong. Keep messages and
                    receipts so disputes can be reviewed fairly.
                </p>
                <DocCallout title="Report a problem">
                    Use the support / report flow in the app for urgent or security-related issues.
                </DocCallout>
            </DocProse>
        ),
    },
};
